import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap-v5'
import SVG from 'react-inlinesvg'
import { useNotificationSettings } from '../../hooks/notifications/useNotificationSettings'
import { NotificationSettings } from '../../services/notificationSettingsService'

// ============================================================================
// PR-I-65 — NotificationsSettingsModal
// ----------------------------------------------------------------------------
// Modal Bootstrap (react-bootstrap-v5) que expone los 6 toggles del endpoint
// /notifications/settings/ (PR-I-63 backend).
//
// Modelo de guardado (post-feedback Armando 2026-09-06):
//   - Al abrir, cargamos los valores del back UNA vez y snapshot en `draft`.
//   - Cada click sobre un toggle solo muta `draft` (state local, sin round-trip).
//   - SAVE hace UN PATCH con el diff draft-vs-baseline y cierra.
//   - CLOSE (o cerrar con X) descarta el draft sin persistir.
//
// Copies en ingles hardcoded (sin i18n en el resto del repo aun). role="switch"
// + aria-checked para AT, label asociado via aria-labelledby.
// ============================================================================

type ToggleKey = keyof NotificationSettings

interface ToggleDef {
  key: ToggleKey
  label: string
}

const EMAIL_TOGGLES: ReadonlyArray<ToggleDef> = [
  {
    key: 'email_new_introduction_requests',
    label: 'Email me when I receive a new introduction request',
  },
  {
    key: 'email_new_forum_messages',
    label: 'Email me when there are new forum messages',
  },
  {
    key: 'email_experience_updates',
    label: 'Email me about experience updates',
  },
  {
    key: 'email_new_member_invitations',
    label: 'Email me when I receive a new member invitation',
  },
]

const MARKETING_TOGGLES: ReadonlyArray<ToggleDef> = [
  {
    key: 'marketing_product_updates',
    label: 'Send me product updates and Nobilis news',
  },
]

const FORUM_TOGGLES: ReadonlyArray<ToggleDef> = [
  {
    key: 'forum_mentions_and_replies',
    label: 'Notify me on forum mentions and replies',
  },
]

interface Props {
  show: boolean
  onClose: () => void
}

const NotificationsSettingsModal: FC<Props> = ({ show, onClose }) => {
  const { settings, loading, error, save, refetch } = useNotificationSettings()

  // `draft` es la copia editable local; `baseline` es lo que el back devolvio
  // al abrir (o tras el ultimo SAVE exitoso). El diff entre ambos alimenta el
  // PATCH y el enabled del boton Save.
  const [draft, setDraft] = useState<NotificationSettings | null>(null)
  const [baseline, setBaseline] = useState<NotificationSettings | null>(null)
  const [saving, setSaving] = useState<boolean>(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Flag: ya inicializamos el draft para esta apertura del modal? Se resetea
  // al cerrar. Evita usar el `settings` cacheado del hook (que puede ser stale
  // de una apertura anterior) antes de que termine el refetch fresco.
  const [initialized, setInitialized] = useState<boolean>(false)

  // Trackea si vimos `loading=true` desde que se abrio el modal. Sin esto, la
  // primera pasada del render del `open` ve el `settings` cacheado del hook
  // (loading=false, settings=<viejo>) y el effect de inicializacion dispara
  // ANTES de que `refetch()` cambie `loading` a true. Resultado: draft=stale.
  // Con este flag, la inicializacion espera al ciclo completo true→false.
  const [sawLoading, setSawLoading] = useState<boolean>(false)

  // Al abrir: refetch al servidor para garantizar que el draft parte de la
  // verdad actual. Al cerrar: reset total (draft, baseline, flags). La proxima
  // apertura re-inicializara solo con la respuesta del refetch (no con cache).
  useEffect(() => {
    if (show) {
      refetch()
    } else {
      setDraft(null)
      setBaseline(null)
      setSaveError(null)
      setSaving(false)
      setInitialized(false)
      setSawLoading(false)
    }
  }, [show, refetch])

  // Marca que ya empezo el fetch fresco (loading=true tras el open). Este
  // es el gatillo que autoriza a la siguiente transicion a false a hidratar
  // el draft con datos frescos, no con el cache stale.
  useEffect(() => {
    if (show && loading) {
      setSawLoading(true)
    }
  }, [show, loading])

  // Cuando el refetch termina (loading pasa a false DESPUES de haber sido true
  // en esta apertura) y tenemos settings frescos, inicializamos draft+baseline
  // UNA VEZ por apertura. El guard `!initialized` evita reinicializar cuando
  // el usuario ya empezo a editar; `sawLoading` evita hidratar con cache stale.
  useEffect(() => {
    if (show && sawLoading && !loading && settings && !initialized) {
      setDraft(settings)
      setBaseline(settings)
      setSaveError(null)
      setInitialized(true)
    }
  }, [show, sawLoading, loading, settings, initialized])

  const handleToggle = useCallback((key: ToggleKey) => {
    setDraft((prev) => {
      if (!prev) return prev
      return { ...prev, [key]: !prev[key] }
    })
    setSaveError(null)
  }, [])

  // Diff draft-vs-baseline: solo mandamos las claves que cambiaron. Si no hay
  // diff, Save es no-op (deshabilitado abajo).
  const diff = useMemo<Partial<NotificationSettings>>(() => {
    if (!draft || !baseline) return {}
    const out: Partial<NotificationSettings> = {}
    ;(Object.keys(draft) as ToggleKey[]).forEach((k) => {
      if (draft[k] !== baseline[k]) {
        ;(out as Record<string, boolean>)[k] = draft[k]
      }
    })
    return out
  }, [draft, baseline])

  const hasChanges = Object.keys(diff).length > 0

  const handleSave = useCallback(async () => {
    if (!hasChanges || saving) return
    setSaving(true)
    setSaveError(null)
    try {
      await save(diff)
      onClose()
    } catch (err) {
      setSaveError("We couldn't save your changes. Please try again.")
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('[notifications-settings] save failed', err)
      }
    } finally {
      setSaving(false)
    }
  }, [diff, hasChanges, saving, save, onClose])

  const renderToggle = (def: ToggleDef) => {
    const inputId = `nb-nsm-toggle-${def.key}`
    const checked = draft ? Boolean(draft[def.key]) : false
    // Switch pintado como <button role=switch> + <span.__toggle-knob> en vez
    // de <input type=checkbox>: los inputs son elementos reemplazados que
    // ignoran pseudo-elementos y a veces se colapsan visualmente bajo
    // appearance:none. Un boton es 100% estilable y semanticamente equivalente.
    return (
      <div key={def.key} className='nb-notifications-settings-modal__row'>
        <span
          id={`${inputId}-label`}
          className='nb-notifications-settings-modal__row-label'
        >
          {def.label}
        </span>
        <button
          id={inputId}
          type='button'
          role='switch'
          aria-checked={checked}
          aria-labelledby={`${inputId}-label`}
          className={
            'nb-notifications-settings-modal__toggle' +
            (checked ? ' nb-notifications-settings-modal__toggle--on' : '')
          }
          disabled={!draft || loading || saving}
          onClick={() => handleToggle(def.key)}
        >
          <span className='nb-notifications-settings-modal__toggle-knob' />
        </button>
      </div>
    )
  }

  const renderSkeleton = () => (
    <div
      className='nb-notifications-settings-modal__skeleton'
      aria-hidden='true'
    >
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={`nsm-skel-${i}`}
          className='nb-notifications-settings-modal__skeleton-row'
        />
      ))}
    </div>
  )

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size='lg'
      backdrop='static'
      dialogClassName='nb-notifications-settings-modal__dialog'
      aria-labelledby='nb-notifications-settings-modal-title'
    >
      <div className='nb-notifications-settings-modal'>
        {/* HEADER */}
        <div className='nb-notifications-settings-modal__header'>
          <h2
            id='nb-notifications-settings-modal-title'
            className='nb-notifications-settings-modal__title'
          >
            Notifications settings
          </h2>
          <button
            type='button'
            className='nb-notifications-settings-modal__close'
            aria-label='Close notifications settings'
            onClick={onClose}
          >
            <SVG src='/media/svg/nobilis/closeX.svg' />
          </button>
        </div>

        {/* BODY */}
        <div className='nb-notifications-settings-modal__body'>
          {error && (
            <div
              className='nb-notifications-settings-modal__error'
              role='alert'
            >
              We couldn't load your notification settings. Please close this
              window and try again.
            </div>
          )}

          {loading && !draft && !error && renderSkeleton()}

          {draft && (
            <>
              <section className='nb-notifications-settings-modal__section'>
                <h3 className='nb-notifications-settings-modal__section-title'>
                  Email notifications
                </h3>
                {EMAIL_TOGGLES.map(renderToggle)}
              </section>

              <section className='nb-notifications-settings-modal__section'>
                <h3 className='nb-notifications-settings-modal__section-title'>
                  Marketing
                </h3>
                {MARKETING_TOGGLES.map(renderToggle)}
              </section>

              <section className='nb-notifications-settings-modal__section'>
                <h3 className='nb-notifications-settings-modal__section-title'>
                  Forum activity
                </h3>
                {FORUM_TOGGLES.map(renderToggle)}
              </section>

              {saveError && (
                <div
                  className='nb-notifications-settings-modal__error nb-notifications-settings-modal__error--inline'
                  role='alert'
                >
                  {saveError}
                </div>
              )}
            </>
          )}
        </div>

        {/*
          FOOTER
          --------------------------------------------------------------
          CLOSE descarta el draft (nunca persiste).
          SAVE manda UN PATCH con las claves cambiadas (diff) y cierra.
          Save queda deshabilitado si no hay diff o si ya hay un save en vuelo.
          --------------------------------------------------------------
        */}
        <div className='nb-notifications-settings-modal__footer'>
          <button
            type='button'
            className='nb-notifications-settings-modal__cancel'
            onClick={onClose}
            disabled={saving}
          >
            Close
          </button>
          <button
            type='button'
            className='nb-notifications-settings-modal__done'
            onClick={handleSave}
            disabled={!hasChanges || saving || loading}
            aria-busy={saving || undefined}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default NotificationsSettingsModal
